import type { FC } from 'react';
import { useMemo } from 'react';
import type { SubmissionShort } from '@queries/fetchSubmissions';
import pluralize from 'pluralize';

import { Footer, VerticalSplitDivider } from '../components';

import { ContractsList } from './ContractsList';
import { ContractsListOpenSearch } from './ContractsListOpenSearch';
import { SelectedContractsBasket } from './SelectedContractsBasket';

interface CloneEndorsementContentProps {
  filteredContracts: SubmissionShort[];
  isCloning: boolean;
  isLoading: boolean;
  onApply: (selectedContractsIds: string[]) => void;
  onCancel: () => void;
  addContract: (contract: SubmissionShort) => void;
  removeContract: (contractId: string) => void;
  searchValue: string;
  setSearchValue: (value: string) => void;
  selectedContracts: SubmissionShort[];
  selectedCount: number;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
}

const getApplyButtonLabel = (selectedCount: number): string =>
  `Apply to ${selectedCount} ${pluralize('Contract', selectedCount)}`;

export const CloneEndorsementContent: FC<CloneEndorsementContentProps> = ({
  filteredContracts,
  isCloning,
  isLoading,
  onApply,
  onCancel,
  addContract,
  removeContract,
  searchValue,
  setSearchValue,
  selectedContracts,
  selectedCount,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}) => {
  'use memo';
  const applyButtonLabel = useMemo(() => getApplyButtonLabel(selectedCount), [selectedCount]);
  const selectedContractIds = selectedContracts.map((contract) => contract.id);

  const handleSubmit = () => onApply(selectedContractIds);

  return (
    <div className="flex flex-col gap-5 pt-1 pb-6">
      <p className="text-info-500 pb-5 text-left text-sm leading-5">
        Select one or more contracts below. The endorsement will be duplicated to all selected.
      </p>
      <div className="flex min-h-[315px] w-full gap-5">
        {fetchNextPage && hasNextPage !== undefined && isFetchingNextPage !== undefined ? (
          <ContractsListOpenSearch
            data={filteredContracts}
            isLoading={isLoading}
            onAddContract={addContract}
            onSearch={setSearchValue}
            searchValue={searchValue}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            fetchNextPage={fetchNextPage}
          />
        ) : (
          <ContractsList
            data={filteredContracts}
            isLoading={isLoading}
            onAddContract={addContract}
            onSearch={setSearchValue}
            searchValue={searchValue}
          />
        )}
        <VerticalSplitDivider />
        <SelectedContractsBasket selectedContracts={selectedContracts} removeContract={removeContract} />
      </div>
      <Footer
        onCancel={onCancel}
        isApplyDisabled={selectedCount === 0 || isCloning}
        applyButtonLabel={applyButtonLabel}
        onSubmit={handleSubmit}
      />
    </div>
  );
};
